import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUsuarioActual } from '@/hooks/useSolicitudesContado';
import { modoDev, msalInstance, scopesApi } from '@/lib/auth';
import { obtenerUsuarioActual } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogoAdelante } from './LogoAdelante';

interface Ruta {
  href: string;
  label: string;
  /** Entrada de menú con hijos: no navega, despliega un submenú. */
  hijos?: { href: string; label: string }[];
}

const RUTAS: Ruta[] = [
  { href: '/vencimientos', label: 'Vencimientos' },
  { href: '/vencimientos/solicitudes', label: 'Solicitudes Contado' },
  {
    href: '/pagos',
    label: 'Pagos',
    hijos: [
      { href: '/vencimientos/propuestas', label: 'Pago Proveedores' },
      { href: '/flujo-semanal', label: 'Pagos Semanales' },
    ],
  },
  {
    href: '/reportes',
    label: 'Reportes',
    hijos: [
      { href: '/flujo-semanal/historial', label: 'Historial' },
      { href: '/flujo-semanal/proyeccion', label: 'Proyección' },
      { href: '/flujo-semanal/proveedores-credito', label: 'Proveedores Crédito' },
    ],
  },
  {
    href: '/flujo-semanal/configuracion',
    label: 'Configuración',
    hijos: [
      { href: '/flujo-semanal/proveedores', label: 'Proveedores' },
      { href: '/flujo-semanal/conceptos', label: 'Conceptos' },
      { href: '/flujo-semanal/categorias', label: 'Categorías' },
      { href: '/flujo-semanal/obligaciones', label: 'Obligaciones' },
      { href: '/flujo-semanal/proveedores-pactados', label: 'Proveedores pactados' },
      { href: '/flujo-semanal/vincular-proveedores', label: 'Vincular Proveedores BC' },
    ],
  },
];

/** Todas las rutas navegables, padres e hijas. El href de un padre con submenú no navega. */
const TODOS_LOS_HREFS = RUTAS.flatMap((r) => (r.hijos ? r.hijos.map((h) => h.href) : [r.href]));

function cerrarSesion() {
  if (modoDev) {
    window.location.reload();
    return;
  }
  msalInstance?.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
}

async function recargarToken() {
  if (modoDev) {
    window.location.reload();
    return;
  }
  try {
    const cuenta = msalInstance?.getAllAccounts()[0];
    if (!cuenta) return;
    await msalInstance?.acquireTokenRedirect({ scopes: scopesApi, account: cuenta });
  } catch {
    window.location.reload();
  }
}

export function HeaderAdelante() {
  const { pathname } = useLocation();
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [navMovilAbierta, setNavMovilAbierta] = useState(false);
  const usuario = obtenerUsuarioActual();
  const { data: yoData } = useUsuarioActual();
  // Perfil Proveeduría: el menú solo ofrece las solicitudes de contado.
  const rutas = yoData?.data?.soloSolicitudes
    ? RUTAS.filter((r) => r.href === '/vencimientos/solicitudes')
    : RUTAS;
  const iniciales = usuario?.iniciales ?? '?';

  // Gana la entrada MÁS específica que cubre la ruta actual: varias comparten prefijo
  // (`/flujo-semanal` con `/flujo-semanal/historial`, `/vencimientos` con
  // `/vencimientos/propuestas`) y marcar todas dejaría dos ítems resaltados a la vez.
  // El match es por segmento completo, para que `/flujo-semanal/proveedores` no se
  // active estando en `/flujo-semanal/proveedores-credito`.
  const hrefActivo = TODOS_LOS_HREFS.filter(
    (href) => pathname === href || pathname.startsWith(`${href}/`),
  ).sort((a, b) => b.length - a.length)[0];

  const esActiva = (href: string) => href === hrefActivo;

  const rutaActiva = (r: Ruta) =>
    r.hijos ? r.hijos.some((h) => esActiva(h.href)) : esActiva(r.href);

  // Cierra el menú móvil al navegar a otra sección
  // biome-ignore lint/correctness/useExhaustiveDependencies: cerrar al cambiar de ruta
  useEffect(() => {
    setNavMovilAbierta(false);
  }, [pathname]);

  return (
    <header className="sticky top-0 z-40 bg-[#0A0A0A] text-white print:hidden">
      <div className="container mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6">
        {/* Logo + nombre */}
        <Link to="/" className="flex items-center gap-2">
          <LogoAdelante altura={22} />
          <span className="text-sm font-bold tracking-wider">ADELANTE.</span>
        </Link>

        {/* Navegación (escritorio) */}
        <nav className="hidden items-center gap-5 lg:flex">
          {rutas.map((r, i) =>
            r.hijos ? (
              <DropdownMenu key={r.href}>
                <DropdownMenuTrigger
                  className={cn(
                    'flex items-center gap-1 text-sm outline-none transition-colors',
                    rutaActiva(r) ? 'font-medium text-white' : 'text-zinc-400 hover:text-white',
                  )}
                >
                  {r.label}
                  <ChevronDown className="h-3.5 w-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align={i === rutas.length - 1 ? 'end' : 'start'}
                  className="w-48"
                >
                  {r.hijos.map((h) => (
                    <DropdownMenuItem key={h.href} asChild>
                      <Link to={h.href} className={cn(esActiva(h.href) && 'font-medium')}>
                        {h.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                key={r.href}
                to={r.href}
                className={cn(
                  'text-sm transition-colors',
                  rutaActiva(r) ? 'font-medium text-white' : 'text-zinc-400 hover:text-white',
                )}
              >
                {r.label}
              </Link>
            ),
          )}
        </nav>

        <div className="flex items-center gap-1">
          {/* Botón hamburguesa (móvil / pantallas angostas) */}
          <button
            type="button"
            onClick={() => setNavMovilAbierta((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-md text-zinc-300 transition-colors hover:bg-zinc-800 hover:text-white lg:hidden"
            aria-label="Menú de navegación"
            aria-expanded={navMovilAbierta}
          >
            {navMovilAbierta ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {/* Avatar con dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuAbierto((v) => !v)}
              onBlur={() => setTimeout(() => setMenuAbierto(false), 150)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-adelante-primary text-xs font-bold text-adelante-foreground transition-transform hover:scale-105"
              title={usuario?.name ?? ''}
              aria-label="Menú de usuario"
            >
              {iniciales}
            </button>

            {menuAbierto && (
              <div className="absolute right-0 top-10 z-50 w-60 rounded-md border border-border bg-white p-1 text-foreground shadow-lg">
                {/* Info del usuario */}
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{usuario?.name ?? 'Sin sesión'}</p>
                  <p className="text-xs text-muted-foreground">{usuario?.username ?? ''}</p>
                </div>

                <div className="my-1 border-t border-border" />

                {/* Recargar token */}
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm transition-colors hover:bg-zinc-50"
                  onMouseDown={() => {
                    setMenuAbierto(false);
                    recargarToken();
                  }}
                >
                  <svg
                    className="h-4 w-4 text-muted-foreground"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Recargar token
                </button>

                {/* Cerrar sesión */}
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-destructive transition-colors hover:bg-red-50"
                  onMouseDown={() => {
                    setMenuAbierto(false);
                    cerrarSesion();
                  }}
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navegación desplegable (móvil / pantallas angostas) */}
      {navMovilAbierta && (
        <nav className="border-t border-zinc-800 bg-[#0A0A0A] px-4 py-2 lg:hidden">
          {rutas.map((r) =>
            r.hijos ? (
              // En móvil no hay dropdown: el padre es un encabezado y los hijos van indentados.
              <div key={r.href} className="mt-1 border-t border-zinc-800/70 pt-1">
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  {r.label}
                </div>
                {r.hijos.map((h) => (
                  <Link
                    key={h.href}
                    to={h.href}
                    onClick={() => setNavMovilAbierta(false)}
                    className={cn(
                      'block rounded-md py-2.5 pl-6 pr-3 text-sm transition-colors',
                      esActiva(h.href)
                        ? 'bg-zinc-800 font-medium text-white'
                        : 'text-zinc-300 hover:bg-zinc-800/60 hover:text-white',
                    )}
                  >
                    {h.label}
                  </Link>
                ))}
              </div>
            ) : (
              <Link
                key={r.href}
                to={r.href}
                onClick={() => setNavMovilAbierta(false)}
                className={cn(
                  'block rounded-md px-3 py-2.5 text-sm transition-colors',
                  rutaActiva(r)
                    ? 'bg-zinc-800 font-medium text-white'
                    : 'text-zinc-300 hover:bg-zinc-800/60 hover:text-white',
                )}
              >
                {r.label}
              </Link>
            ),
          )}
        </nav>
      )}
    </header>
  );
}
